/**
 * Customer Library — buyer-facing portal.
 * Route: /library (protected), /library/login, /library/signup,
 *        /library/forgot, /library/reset-password?token=XYZ
 *
 * Design system: Freedom Funding Readiness™ (dark forest + gold).
 * Auth: cookie-based JWT (backend sets `library_token` httpOnly). Also
 * sends the same token in Authorization: Bearer as a fallback belt-and-suspenders.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, Navigate, Route, Routes, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowRight, LogOut, Lock, Mail, User, Download, ShoppingBag,
  ExternalLink, Loader2, ShieldCheck, ChevronRight, KeyRound, CheckCircle2,
} from "lucide-react";
import { useSiteContent } from "@/lib/siteContent";

const API = process.env.REACT_APP_BACKEND_URL;

// Palette (matches FundingReadiness / Shop)
const FFR = {
  forest: "#0B3D2E",
  forestDeep: "#062218",
  gold: "#C9A75A",
  goldSoft: "#F5EEDA",
  ivory: "#FAF7EE",
  parchment: "#F1ECDE",
};

/* --------------------------- API helpers --------------------------- */

// Store token in localStorage as belt-and-suspenders so it works even if the
// httpOnly cookie fails (e.g. Safari ITP or cross-subdomain preview edge cases).
const LS_KEY = "ffr_library_token";
const getToken = () => { try { return localStorage.getItem(LS_KEY) || ""; } catch { return ""; } };
const setToken = (t) => { try { t ? localStorage.setItem(LS_KEY, t) : localStorage.removeItem(LS_KEY); } catch { /* ignore */ } };

async function api(path, { method = "GET", body, auth = false } = {}) {
  const headers = { "Content-Type": "application/json" };
  if (auth) {
    const t = getToken();
    if (t) headers["Authorization"] = `Bearer ${t}`;
  }
  const res = await fetch(`${API}${path}`, {
    method,
    headers,
    credentials: "include",
    body: body ? JSON.stringify(body) : undefined,
  });
  let data = null;
  try { data = await res.json(); } catch { /* empty */ }
  if (!res.ok) {
    const detail = data?.detail;
    const msg = Array.isArray(detail)
      ? detail.map((e) => e?.msg || JSON.stringify(e)).join(" ")
      : (typeof detail === "string" ? detail : (detail?.msg || `Request failed (${res.status})`));
    const err = new Error(msg);
    err.status = res.status;
    throw err;
  }
  return data;
}

/* --------------------------- Shared UI --------------------------- */

function BrandShell({ children, showLogout = false, onLogout }) {
  return (
    <div className="min-h-screen" style={{ background: FFR.forestDeep, fontFamily: "'Inter', -apple-system, 'Segoe UI', system-ui, sans-serif" }}>
      <header className="border-b" style={{ borderColor: `${FFR.gold}22`, background: "rgba(11,61,46,0.88)", backdropFilter: "blur(12px)" }}>
        <div className="max-w-[1240px] mx-auto px-6 lg:px-10 py-4 flex items-center justify-between">
          <Link to="/funding-readiness" className="flex items-center gap-2.5 group" data-testid="library-brand-link">
            <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: FFR.gold }}>
              <ShoppingBag size={16} style={{ color: FFR.forestDeep }} />
            </div>
            <div className="leading-tight">
              <div className="text-[10px] uppercase font-black tracking-[0.2em]" style={{ color: FFR.gold }}>Freedom Funding Readiness™</div>
              <div className="text-[13px] font-bold text-white -mt-0.5">My Library</div>
            </div>
          </Link>
          {showLogout && (
            <button
              onClick={onLogout}
              data-testid="library-logout-btn"
              className="text-[12px] uppercase tracking-[0.16em] font-bold flex items-center gap-1.5 px-3 py-2 rounded-md hover:bg-white/5 transition-colors"
              style={{ color: FFR.gold }}
            >
              <LogOut size={13} /> Log out
            </button>
          )}
        </div>
      </header>
      {children}
    </div>
  );
}

function AuthCard({ title, subtitle, children }) {
  return (
    <BrandShell>
      <main className="flex items-center justify-center px-6 py-16">
        <div className="w-full max-w-[440px]">
          <div className="text-[10px] uppercase font-black tracking-[0.22em] mb-3 text-center" style={{ color: FFR.gold }}>
            Members portal
          </div>
          <h1 className="font-black uppercase tracking-[-0.02em] leading-[1.05] text-center" style={{ fontSize: "clamp(30px, 4.4vw, 44px)", color: "white" }}>
            {title}
          </h1>
          {subtitle && (
            <p className="text-[14px] text-center mt-3" style={{ color: "rgba(255,255,255,0.65)" }}>{subtitle}</p>
          )}
          <div className="mt-8 rounded-3xl p-7 lg:p-8 border-2" style={{ background: "white", borderColor: `${FFR.gold}55` }}>
            {children}
          </div>
        </div>
      </main>
    </BrandShell>
  );
}

function Field({ label, id, icon: Icon, ...props }) {
  return (
    <label htmlFor={id} className="block">
      <div className="text-[10px] uppercase font-bold tracking-[0.2em] mb-1.5" style={{ color: FFR.forest }}>{label}</div>
      <div className="relative">
        {Icon && (
          <div className="absolute left-3.5 top-1/2 -translate-y-1/2 pointer-events-none" style={{ color: `${FFR.forest}88` }}>
            <Icon size={16} />
          </div>
        )}
        <input
          id={id}
          {...props}
          className="w-full rounded-xl border-2 py-3 px-4 text-[15px] font-medium outline-none transition-all focus:border-current"
          style={{ borderColor: `${FFR.gold}55`, background: FFR.parchment, color: FFR.forestDeep, paddingLeft: Icon ? 40 : 16 }}
        />
      </div>
    </label>
  );
}

function GoldButton({ children, loading, testid, ...props }) {
  return (
    <button
      data-testid={testid}
      disabled={loading || props.disabled}
      {...props}
      className="w-full rounded-full py-3.5 font-black uppercase tracking-[0.14em] text-[13px] flex items-center justify-center gap-2 transition-all hover:opacity-95 disabled:opacity-60 disabled:cursor-not-allowed"
      style={{ background: FFR.gold, color: FFR.forestDeep }}
    >
      {loading ? <Loader2 size={16} className="animate-spin" /> : children}
    </button>
  );
}

/* --------------------------- Signup / Login / Forgot / Reset --------------------------- */

function useAuth(navigate) {
  const [me, setMe] = useState(null);        // null = checking, false = anon, obj = signed in
  const check = useCallback(async () => {
    try {
      const data = await api("/api/library/me", { auth: true });
      setMe(data.customer);
    } catch (e) {
      setMe(false);
    }
  }, []);
  useEffect(() => { check(); }, [check]);

  const login = async (email, password) => {
    const data = await api("/api/library/login", { method: "POST", body: { email, password } });
    if (data?.token) setToken(data.token);
    setMe(data.customer);
    return data.customer;
  };
  const register = async (email, password, name) => {
    const data = await api("/api/library/register", { method: "POST", body: { email, password, name } });
    if (data?.token) setToken(data.token);
    setMe(data.customer);
    return data.customer;
  };
  const logout = async () => {
    try { await api("/api/library/logout", { method: "POST", auth: true }); } catch { /* ignore */ }
    setToken("");
    setMe(false);
    if (navigate) navigate("/library/login", { replace: true });
  };
  return { me, login, register, logout, refresh: check };
}

function Signup({ auth }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 8) { toast.error("Password must be at least 8 characters."); return; }
    setLoading(true);
    try {
      await auth.register(email.trim(), password, name.trim());
      toast.success("Account created — welcome!");
      navigate("/library", { replace: true });
    } catch (e) { toast.error(e.message || "Sign up failed"); }
    finally { setLoading(false); }
  };

  return (
    <AuthCard title="Create your account" subtitle="Use the email you used at checkout so your purchases show up automatically.">
      <form onSubmit={submit} className="space-y-4" data-testid="library-signup-form">
        <Field label="Your name" id="signup-name" icon={User} type="text" value={name} onChange={(e) => setName(e.target.value)} placeholder="First & last" autoComplete="name" data-testid="library-signup-name" />
        <Field label="Email" id="signup-email" icon={Mail} type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" autoComplete="email" data-testid="library-signup-email" />
        <Field label="Password" id="signup-password" icon={Lock} type="password" required minLength={8} value={password} onChange={(e) => setPassword(e.target.value)} placeholder="At least 8 characters" autoComplete="new-password" data-testid="library-signup-password" />
        <GoldButton type="submit" loading={loading} testid="library-signup-submit">
          Create account <ArrowRight size={14} />
        </GoldButton>
        <div className="text-[13px] text-center pt-2" style={{ color: `${FFR.forestDeep}aa` }}>
          Already have an account?{" "}
          <Link to="/library/login" className="font-bold underline" style={{ color: FFR.forest }} data-testid="library-signup-login-link">
            Log in
          </Link>
        </div>
      </form>
    </AuthCard>
  );
}

function Login({ auth }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from || "/library";

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await auth.login(email.trim(), password);
      toast.success("Welcome back!");
      navigate(from, { replace: true });
    } catch (e) { toast.error(e.message || "Login failed"); }
    finally { setLoading(false); }
  };

  return (
    <AuthCard title="Log in to your library" subtitle="Your purchased toolkits, receipts, and updates from Donja.">
      <form onSubmit={submit} className="space-y-4" data-testid="library-login-form">
        <Field label="Email" id="login-email" icon={Mail} type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" autoComplete="email" data-testid="library-login-email" />
        <Field label="Password" id="login-password" icon={Lock} type="password" required value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" autoComplete="current-password" data-testid="library-login-password" />
        <GoldButton type="submit" loading={loading} testid="library-login-submit">
          Log in <ArrowRight size={14} />
        </GoldButton>
        <div className="flex items-center justify-between text-[12.5px] pt-1" style={{ color: `${FFR.forestDeep}aa` }}>
          <Link to="/library/forgot" className="font-semibold underline" style={{ color: FFR.forest }} data-testid="library-forgot-link">
            Forgot password?
          </Link>
          <Link to="/library/signup" className="font-semibold underline" style={{ color: FFR.forest }} data-testid="library-signup-link">
            Create account
          </Link>
        </div>
      </form>
    </AuthCard>
  );
}

function Forgot() {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api("/api/library/forgot-password", { method: "POST", body: { email: email.trim() } });
      setSent(true);
    } catch (e) { toast.error(e.message || "Something went wrong"); }
    finally { setLoading(false); }
  };

  if (sent) {
    return (
      <AuthCard title="Check your inbox" subtitle="If an account exists with that email, we've sent a link to reset your password.">
        <div className="text-center">
          <div className="w-14 h-14 rounded-full mx-auto flex items-center justify-center" style={{ background: `${FFR.gold}22` }}>
            <Mail size={22} style={{ color: FFR.forest }} />
          </div>
          <p className="mt-5 text-[14.5px] leading-[1.6]" style={{ color: FFR.forestDeep }}>
            The reset link expires in one hour. If you don&apos;t see the email in a few minutes, check your spam folder.
          </p>
          <Link to="/library/login" className="mt-6 inline-flex items-center gap-1.5 font-bold text-[13px] uppercase tracking-[0.14em]" style={{ color: FFR.forest }} data-testid="library-forgot-back-login">
            Back to login <ChevronRight size={14} />
          </Link>
        </div>
      </AuthCard>
    );
  }
  return (
    <AuthCard title="Reset your password" subtitle="Enter the email you used to sign up and we'll send you a reset link.">
      <form onSubmit={submit} className="space-y-4" data-testid="library-forgot-form">
        <Field label="Email" id="forgot-email" icon={Mail} type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" autoComplete="email" data-testid="library-forgot-email" />
        <GoldButton type="submit" loading={loading} testid="library-forgot-submit">
          Send reset link <ArrowRight size={14} />
        </GoldButton>
        <div className="text-[13px] text-center pt-1" style={{ color: `${FFR.forestDeep}aa` }}>
          <Link to="/library/login" className="font-semibold underline" style={{ color: FFR.forest }}>
            Back to login
          </Link>
        </div>
      </form>
    </AuthCard>
  );
}

function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 8) { toast.error("Password must be at least 8 characters."); return; }
    if (!token) { toast.error("Missing reset token."); return; }
    setLoading(true);
    try {
      const data = await api("/api/library/reset-password", { method: "POST", body: { token, password } });
      if (data?.token) setToken(data.token);
      toast.success("Password updated — you're now signed in.");
      navigate("/library", { replace: true });
    } catch (e) { toast.error(e.message || "Reset failed"); }
    finally { setLoading(false); }
  };

  if (!token) {
    return (
      <AuthCard title="Reset link is missing">
        <p className="text-center text-[14.5px]" style={{ color: FFR.forestDeep }}>
          This reset link is invalid. <Link to="/library/forgot" className="font-bold underline" style={{ color: FFR.forest }}>Request a new one</Link>.
        </p>
      </AuthCard>
    );
  }
  return (
    <AuthCard title="Choose a new password" subtitle="Pick something you'll remember. We'll sign you in automatically.">
      <form onSubmit={submit} className="space-y-4" data-testid="library-reset-form">
        <Field label="New password" id="reset-password" icon={KeyRound} type="password" required minLength={8} value={password} onChange={(e) => setPassword(e.target.value)} placeholder="At least 8 characters" autoComplete="new-password" data-testid="library-reset-password" />
        <GoldButton type="submit" loading={loading} testid="library-reset-submit">
          Update password <ArrowRight size={14} />
        </GoldButton>
      </form>
    </AuthCard>
  );
}

/* --------------------------- Library Dashboard --------------------------- */

function currency(cents, ccy) {
  if (cents == null) return "";
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: (ccy || "usd").toUpperCase(),
    minimumFractionDigits: 0,
  }).format((cents || 0) / 100);
}

function formatDate(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" });
  } catch { return ""; }
}

function EmptyState({ customer }) {
  return (
    <div className="rounded-3xl p-8 lg:p-12 text-center" style={{ background: "white", border: `2px solid ${FFR.gold}44` }} data-testid="library-empty-state">
      <div className="w-16 h-16 rounded-full mx-auto flex items-center justify-center" style={{ background: `${FFR.gold}22` }}>
        <ShoppingBag size={26} style={{ color: FFR.forest }} />
      </div>
      <h2 className="mt-5 font-black uppercase tracking-[-0.01em]" style={{ fontSize: "clamp(22px, 2.6vw, 30px)", color: FFR.forest }}>
        Thank you for being here, <span style={{ color: FFR.gold }}>{(customer.name || "friend").split(" ")[0]}</span>.
      </h2>
      <p className="mt-3 max-w-lg mx-auto text-[15px] leading-[1.65]" style={{ color: FFR.forestDeep }}>
        Your library is where every toolkit, replay, and receipt from Donja will live once you purchase or attend a call.
        Ready to get started? Browse the catalog below.
      </p>
      <div className="mt-6 flex flex-wrap justify-center gap-3">
        <Link to="/shop" className="rounded-full px-6 py-3 font-black uppercase tracking-[0.14em] text-[12.5px] flex items-center gap-1.5" style={{ background: FFR.gold, color: FFR.forestDeep }} data-testid="library-empty-shop-cta">
          Browse the Resource Shop <ArrowRight size={13} />
        </Link>
        <Link to="/funding-readiness" className="rounded-full px-6 py-3 font-black uppercase tracking-[0.14em] text-[12.5px] flex items-center gap-1.5 border-2" style={{ borderColor: FFR.forest, color: FFR.forest }} data-testid="library-empty-fr-cta">
          Explore the Academy <ArrowRight size={13} />
        </Link>
      </div>
    </div>
  );
}

function ChooseThreePicker({ picked, picksRemaining, ownedSkus, catalog, onSaved }) {
  const [selected, setSelected] = useState([]);
  const [loading, setLoading] = useState(false);
  const alreadyOwned = new Set(ownedSkus || []);
  const available = (catalog?.toolkits || []).filter((t) =>
    t.toolkit_sku.startsWith("TK") && !alreadyOwned.has(t.toolkit_sku)
  );

  const toggle = (sku) => {
    setSelected((s) => {
      if (s.includes(sku)) return s.filter((x) => x !== sku);
      if (s.length >= picksRemaining) { toast.error(`You can pick at most ${picksRemaining} toolkit${picksRemaining === 1 ? "" : "s"}.`); return s; }
      return [...s, sku];
    });
  };

  const submit = async () => {
    if (selected.length === 0) { toast.error("Pick at least one toolkit."); return; }
    setLoading(true);
    try {
      await api("/api/library/choose-three", { method: "POST", auth: true, body: { picks: selected } });
      toast.success(`Unlocked ${selected.length} toolkit${selected.length === 1 ? "" : "s"} — enjoy!`);
      onSaved();
    } catch (e) { toast.error(e.message || "Couldn't save your picks."); }
    finally { setLoading(false); }
  };

  return (
    <div className="rounded-3xl p-6 lg:p-8 mb-6 relative overflow-hidden" style={{ background: `linear-gradient(140deg, ${FFR.gold} 0%, #E4CD8F 100%)` }} data-testid="library-choose-three-card">
      <div className="text-[10px] uppercase font-black tracking-[0.22em]" style={{ color: FFR.forestDeep }}>Choose Your Three</div>
      <h2 className="mt-1 font-black uppercase leading-tight" style={{ fontSize: "clamp(22px, 2.6vw, 30px)", color: FFR.forestDeep }}>
        Pick <span style={{ color: FFR.forest }}>{picksRemaining}</span> more toolkit{picksRemaining === 1 ? "" : "s"} to unlock.
      </h2>
      <p className="mt-2 text-[14px]" style={{ color: FFR.forestDeep }}>
        Your bundle lets you choose {picksRemaining} of the $27 toolkits below. Select them here — they&apos;ll be added to your library instantly.
      </p>
      {picked && picked.length > 0 && (
        <div className="mt-3 text-[12px] font-semibold" style={{ color: FFR.forestDeep }}>
          Already picked: {picked.length}
        </div>
      )}
      <div className="mt-5 grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {available.map((tk) => {
          const isSelected = selected.includes(tk.toolkit_sku);
          return (
            <button
              key={tk.toolkit_sku}
              onClick={() => toggle(tk.toolkit_sku)}
              className="text-left rounded-xl p-4 border-2 transition-all"
              style={{
                background: isSelected ? FFR.forest : "white",
                borderColor: isSelected ? FFR.forestDeep : `${FFR.forest}22`,
                color: isSelected ? "white" : FFR.forestDeep,
              }}
              data-testid={`choose-three-pick-${tk.toolkit_sku}`}
            >
              <div className="text-[10px] uppercase font-bold tracking-[0.18em] mb-1" style={{ color: isSelected ? FFR.gold : FFR.gold }}>Toolkit</div>
              <div className="font-black uppercase text-[14px] leading-tight">{tk.title}</div>
              {tk.tagline && <div className="text-[12px] mt-1 opacity-85">{tk.tagline}</div>}
              <div className="mt-3 text-[10.5px] uppercase font-bold tracking-[0.14em] flex items-center gap-1">
                {isSelected ? "✓ Selected" : "Tap to select"}
              </div>
            </button>
          );
        })}
      </div>
      <div className="mt-5 flex items-center justify-between gap-3 flex-wrap">
        <div className="text-[12.5px] font-semibold" style={{ color: FFR.forestDeep }}>
          {selected.length} / {picksRemaining} selected
        </div>
        <button
          onClick={submit}
          disabled={loading || selected.length === 0}
          className="rounded-full px-6 py-3 font-black uppercase tracking-[0.14em] text-[12px] flex items-center gap-1.5 disabled:opacity-60 disabled:cursor-not-allowed"
          style={{ background: FFR.forestDeep, color: FFR.gold }}
          data-testid="choose-three-submit"
        >
          {loading ? <Loader2 size={13} className="animate-spin" /> : <>Unlock these toolkits <ArrowRight size={13} /></>}
        </button>
      </div>
    </div>
  );
}

function CatalogCard() {
  return (
    <div className="mt-10 rounded-3xl p-7 lg:p-8" style={{ background: FFR.parchment, border: `1px solid ${FFR.gold}55` }} data-testid="library-catalog-card">
      <div className="text-[10px] uppercase font-black tracking-[0.22em] mb-2" style={{ color: FFR.gold }}>Explore more</div>
      <h3 className="font-black uppercase leading-tight tracking-[-0.01em]" style={{ fontSize: "clamp(20px, 2.2vw, 26px)", color: FFR.forest }}>
        Add to your library
      </h3>
      <p className="mt-2 text-[14px]" style={{ color: FFR.forestDeep }}>
        Every toolkit, bundle, and coaching option in one place.
      </p>
      <div className="mt-5 grid sm:grid-cols-3 gap-3">
        <Link to="/shop" className="rounded-xl p-4 group hover:shadow-md transition-all border-2" style={{ background: "white", borderColor: `${FFR.gold}55` }} data-testid="catalog-shop">
          <div className="font-black uppercase text-[13px] tracking-[0.06em]" style={{ color: FFR.forest }}>Resource Shop</div>
          <div className="text-[12px] mt-1" style={{ color: FFR.forestDeep }}>Toolkits from $27, bundles from $67</div>
          <div className="mt-2 text-[11px] uppercase font-bold tracking-[0.14em] flex items-center gap-1" style={{ color: FFR.gold }}>Browse <ChevronRight size={12} /></div>
        </Link>
        <Link to="/funding-readiness#work-with-me" className="rounded-xl p-4 hover:shadow-md transition-all border-2" style={{ background: "white", borderColor: `${FFR.gold}55` }} data-testid="catalog-coaching">
          <div className="font-black uppercase text-[13px] tracking-[0.06em]" style={{ color: FFR.forest }}>Work With Donja</div>
          <div className="text-[12px] mt-1" style={{ color: FFR.forestDeep }}>Group coaching from $99/mo</div>
          <div className="mt-2 text-[11px] uppercase font-bold tracking-[0.14em] flex items-center gap-1" style={{ color: FFR.gold }}>Explore <ChevronRight size={12} /></div>
        </Link>
        <Link to="/funding-readiness#build-lab" className="rounded-xl p-4 hover:shadow-md transition-all border-2" style={{ background: "white", borderColor: `${FFR.gold}55` }} data-testid="catalog-lab">
          <div className="font-black uppercase text-[13px] tracking-[0.06em]" style={{ color: FFR.forest }}>AI Build Lab</div>
          <div className="text-[12px] mt-1" style={{ color: FFR.forestDeep }}>Live 2-hour build session</div>
          <div className="mt-2 text-[11px] uppercase font-bold tracking-[0.14em] flex items-center gap-1" style={{ color: FFR.gold }}>Details <ChevronRight size={12} /></div>
        </Link>
      </div>
    </div>
  );
}

function PurchaseRow({ purchase }) {
  return (
    <div className="rounded-xl p-4 flex flex-col sm:flex-row sm:items-center gap-3 border" style={{ background: "rgba(255,255,255,0.03)", borderColor: `${FFR.gold}22` }} data-testid={`library-purchase-${purchase.event_id}`}>
      <div className="flex-1 min-w-0">
        <div className="text-[10px] uppercase font-bold tracking-[0.22em]" style={{ color: FFR.gold }}>
          {formatDate(purchase.created_at)}
        </div>
        <div className="mt-0.5 font-bold text-[14px] text-white/95 leading-tight">
          {purchase.product_label || "Freedom Funding Readiness order"}
        </div>
      </div>
      <div className="text-[13px] font-bold" style={{ color: FFR.gold }}>
        {currency(purchase.amount_total, purchase.currency)}
      </div>
    </div>
  );
}

function ToolkitCard({ toolkit, onReviewClick }) {
  const [open, setOpen] = useState(false);
  const handleDownload = async (fileSku, preview = false) => {
    try {
      const t = (typeof getToken === "function" ? getToken() : "") || "";
      const res = await fetch(`${API}/api/library/download/${fileSku}${preview ? "?preview=true" : ""}`, {
        headers: t ? { Authorization: `Bearer ${t}` } : {},
        credentials: "include",
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || `Download failed (${res.status})`);
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const cd = res.headers.get("content-disposition") || "";
      const m = cd.match(/filename="?([^";]+)"?/);
      a.download = m ? m[1] : `${fileSku}${preview ? "-preview.pdf" : ""}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
    } catch (e) {
      toast.error(e.message || "Download failed");
    }
  };

  return (
    <div className="rounded-2xl border-2 overflow-hidden" style={{ background: "white", borderColor: `${FFR.gold}55` }} data-testid={`library-toolkit-${toolkit.toolkit_sku}`}>
      <button
        onClick={() => setOpen((v) => !v)}
        className="w-full p-5 lg:p-6 flex items-center gap-4 text-left hover:bg-black/[0.02] transition-colors"
        data-testid={`library-toolkit-toggle-${toolkit.toolkit_sku}`}
      >
        <div className="w-11 h-11 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: `${FFR.gold}22` }}>
          <ShoppingBag size={20} style={{ color: FFR.forest }} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase font-bold tracking-[0.22em]" style={{ color: FFR.gold }}>Toolkit</div>
          <div className="font-black uppercase leading-tight mt-0.5" style={{ fontSize: "17px", color: FFR.forest }}>{toolkit.title}</div>
          {toolkit.tagline && <div className="text-[12.5px] mt-0.5" style={{ color: `${FFR.forestDeep}aa` }}>{toolkit.tagline}</div>}
        </div>
        <div className="text-[11px] uppercase font-bold tracking-[0.14em] flex items-center gap-1" style={{ color: FFR.forest }}>
          {(toolkit.templates || []).length} files
          <ChevronRight size={14} style={{ transform: open ? "rotate(90deg)" : "rotate(0deg)", transition: "transform 0.15s" }} />
        </div>
      </button>
      {open && (
        <div className="border-t px-5 lg:px-6 py-4 space-y-2" style={{ borderColor: `${FFR.gold}22`, background: FFR.parchment }} data-testid={`library-toolkit-files-${toolkit.toolkit_sku}`}>
          {(toolkit.templates || []).map((t) => (
            <div key={t.file_sku} className="flex flex-col sm:flex-row sm:items-center gap-2 py-2 border-b last:border-0" style={{ borderColor: `${FFR.gold}22` }}>
              <div className="flex-1 min-w-0">
                <div className="font-bold text-[14px]" style={{ color: FFR.forest }}>{t.template_name}</div>
                <div className="text-[11.5px]" style={{ color: `${FFR.forestDeep}aa` }}>{t.recipient_role || "General"}</div>
              </div>
              <div className="flex items-center gap-2">
                {t.has_preview && (
                  <button
                    onClick={() => handleDownload(t.file_sku, true)}
                    className="rounded-full px-3.5 py-1.5 text-[11px] uppercase font-bold tracking-[0.12em] border-2"
                    style={{ borderColor: FFR.forest, color: FFR.forest }}
                    data-testid={`library-preview-${t.file_sku}`}
                  >
                    Preview
                  </button>
                )}
                <button
                  onClick={() => handleDownload(t.file_sku)}
                  className="rounded-full px-4 py-1.5 text-[11px] uppercase font-black tracking-[0.14em] flex items-center gap-1"
                  style={{ background: FFR.gold, color: FFR.forestDeep }}
                  data-testid={`library-download-${t.file_sku}`}
                >
                  <Download size={11} /> Download
                </button>
              </div>
            </div>
          ))}
          <div className="pt-3">
            <button
              onClick={() => onReviewClick(toolkit)}
              className="text-[11px] uppercase font-bold tracking-[0.14em] underline"
              style={{ color: FFR.forest }}
              data-testid={`library-review-btn-${toolkit.toolkit_sku}`}
            >
              Leave a review for this toolkit
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function ReviewModal({ toolkit, onClose, onSubmitted }) {
  const [rating, setRating] = useState(5);
  const [body, setBody] = useState("");
  const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (body.trim().length < 6) { toast.error("Please share at least a sentence."); return; }
    setLoading(true);
    try {
      await api("/api/library/reviews", {
        method: "POST",
        auth: true,
        body: { rating, body: body.trim(), product_sku: toolkit.toolkit_sku },
      });
      toast.success("Thank you — your review is queued for approval.");
      onSubmitted();
    } catch (e) { toast.error(e.message || "Review failed to send"); }
    finally { setLoading(false); }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-4" style={{ background: "rgba(6,34,24,0.75)" }} data-testid="library-review-modal">
      <div className="w-full max-w-md rounded-3xl p-6 lg:p-7" style={{ background: "white" }}>
        <div className="text-[10px] uppercase font-black tracking-[0.22em]" style={{ color: FFR.gold }}>Leave a review</div>
        <h3 className="font-black uppercase leading-tight mt-1" style={{ fontSize: "20px", color: FFR.forest }}>{toolkit.title}</h3>
        <form onSubmit={submit} className="space-y-4 mt-5" data-testid="library-review-form">
          <div>
            <div className="text-[10px] uppercase font-bold tracking-[0.2em] mb-1.5" style={{ color: FFR.forest }}>Your rating</div>
            <div className="flex gap-1.5">
              {[1, 2, 3, 4, 5].map((n) => (
                <button
                  type="button"
                  key={n}
                  onClick={() => setRating(n)}
                  className="text-[26px] leading-none"
                  style={{ color: n <= rating ? FFR.gold : `${FFR.forestDeep}22` }}
                  data-testid={`review-star-${n}`}
                  aria-label={`${n} stars`}
                >
                  ★
                </button>
              ))}
            </div>
          </div>
          <div>
            <div className="text-[10px] uppercase font-bold tracking-[0.2em] mb-1.5" style={{ color: FFR.forest }}>Your words</div>
            <textarea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              rows={4}
              maxLength={1200}
              placeholder="What did you love? What changed? Be specific — it helps other nonprofit leaders."
              className="w-full rounded-xl border-2 py-3 px-4 text-[14px] outline-none resize-none"
              style={{ borderColor: `${FFR.gold}55`, background: FFR.parchment, color: FFR.forestDeep }}
              data-testid="library-review-body"
            />
            <div className="text-[11px] mt-1 text-right" style={{ color: `${FFR.forestDeep}88` }}>{body.length} / 1200</div>
          </div>
          <div className="flex items-center gap-2 justify-end pt-1">
            <button type="button" onClick={onClose} className="rounded-full px-5 py-2.5 text-[12px] uppercase font-bold tracking-[0.14em]" style={{ color: FFR.forestDeep }} data-testid="library-review-cancel">
              Cancel
            </button>
            <GoldButton type="submit" loading={loading} testid="library-review-submit">
              Send review <ArrowRight size={13} />
            </GoldButton>
          </div>
        </form>
      </div>
    </div>
  );
}

function LibraryHome({ auth }) {
  const [purchases, setPurchases] = useState(null);
  const [entitlements, setEntitlements] = useState(null);   // { toolkit_skus, toolkits, choose_three }
  const [catalog, setCatalog] = useState(null);
  const [replay, setReplay] = useState(null);   // { owns_replay, youtube_url }
  const [error, setError] = useState("");
  const [reviewFor, setReviewFor] = useState(null);

  const load = useCallback(async () => {
    try {
      const [pur, ent, cat, rep] = await Promise.all([
        api("/api/library/purchases", { auth: true }),
        api("/api/library/entitlements", { auth: true }),
        api("/api/library/catalog"),
        api("/api/library/replay-info", { auth: true }),
      ]);
      setPurchases(pur.purchases || []);
      setEntitlements(ent);
      setCatalog(cat);
      setReplay(rep);
    } catch (e) {
      setError(e.message || "Couldn't load your library");
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const total = useMemo(() => (purchases || []).reduce((a, p) => a + (p.amount_total || 0), 0), [purchases]);
  const toolkits = entitlements?.toolkits || [];
  const choose3 = entitlements?.choose_three;
  const hasAnyPurchase = (purchases && purchases.length > 0) || (toolkits.length > 0) || (replay?.owns_replay);

  const downloadReplayWorkbook = async () => {
    try {
      const t = getToken() || "";
      const res = await fetch(`${API}/api/library/replay-workbook`, {
        headers: t ? { Authorization: `Bearer ${t}` } : {},
        credentials: "include",
      });
      if (!res.ok) throw new Error("Download failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "Beyond-the-Grant.pdf";
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
    } catch (e) { toast.error(e.message || "Download failed"); }
  };

  const markReplayWatched = async () => {
    try {
      const data = await api("/api/library/replay-watched", { method: "POST", auth: true });
      setReplay((r) => ({ ...(r || {}), watched: true, watched_at: data.watched_at }));
      toast.success("Nice work — marked as watched. Ready for the next step?");
    } catch (e) { toast.error(e.message || "Couldn't update watch status"); }
  };

  const downloadStartHere = async () => {
    try {
      const t = getToken() || "";
      const res = await fetch(`${API}/api/library/start-here`, {
        headers: t ? { Authorization: `Bearer ${t}` } : {},
        credentials: "include",
      });
      if (!res.ok) throw new Error("Download failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "00_START-HERE-Guide.pdf";
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
    } catch (e) { toast.error(e.message || "Download failed"); }
  };

  return (
    <BrandShell showLogout onLogout={auth.logout}>
      <main className="max-w-[1080px] mx-auto px-6 lg:px-10 py-10 lg:py-14">
        <div className="mb-8">
          <div className="text-[10px] uppercase font-black tracking-[0.22em] mb-2" style={{ color: FFR.gold }}>My Library</div>
          <h1 className="font-black uppercase leading-[1.02] tracking-[-0.02em]" style={{ fontSize: "clamp(30px, 4.4vw, 52px)", color: "white" }}>
            Welcome back, <span style={{ color: FFR.gold }}>{(auth.me?.name || auth.me?.email || "").split(" ")[0]}</span>.
          </h1>
          <p className="mt-3 text-[15px]" style={{ color: "rgba(255,255,255,0.7)" }}>
            Everything you&apos;ve purchased from Freedom Funding Readiness™ lives here.
          </p>
        </div>

        {purchases === null && !error && (
          <div className="rounded-3xl p-10 text-center" style={{ background: "rgba(255,255,255,0.04)", border: `1px dashed ${FFR.gold}55` }}>
            <Loader2 className="animate-spin mx-auto" size={22} style={{ color: FFR.gold }} />
            <p className="mt-3 text-[13px]" style={{ color: "rgba(255,255,255,0.6)" }}>Loading your library…</p>
          </div>
        )}

        {error && (
          <div className="rounded-2xl p-5" style={{ background: "rgba(220,60,60,0.12)", border: "1px solid rgba(220,60,60,0.4)" }} data-testid="library-error">
            <div className="text-[13px] text-white">{error}</div>
          </div>
        )}

        {purchases && !hasAnyPurchase && <EmptyState customer={auth.me} />}

        {hasAnyPurchase && (
          <>
            <div className="grid sm:grid-cols-3 gap-3 mb-6">
              <div className="rounded-2xl p-4" style={{ background: "rgba(255,255,255,0.05)", border: `1px solid ${FFR.gold}33` }}>
                <div className="text-[10px] uppercase font-bold tracking-[0.2em]" style={{ color: FFR.gold }}>Toolkits unlocked</div>
                <div className="mt-1 font-black text-[26px] text-white">{toolkits.length}</div>
              </div>
              <div className="rounded-2xl p-4" style={{ background: "rgba(255,255,255,0.05)", border: `1px solid ${FFR.gold}33` }}>
                <div className="text-[10px] uppercase font-bold tracking-[0.2em]" style={{ color: FFR.gold }}>Lifetime value</div>
                <div className="mt-1 font-black text-[26px] text-white">{currency(total, purchases?.[0]?.currency)}</div>
              </div>
              <div className="rounded-2xl p-4" style={{ background: "rgba(255,255,255,0.05)", border: `1px solid ${FFR.gold}33` }}>
                <div className="text-[10px] uppercase font-bold tracking-[0.2em]" style={{ color: FFR.gold }}>Purchases</div>
                <div className="mt-1 font-black text-[26px] text-white">{purchases?.length || 0}</div>
              </div>
            </div>

            {toolkits.length > 0 && (
              <div className="mb-6 rounded-2xl p-5 flex items-center gap-4 border" style={{ background: "rgba(201,167,90,0.09)", borderColor: `${FFR.gold}55` }} data-testid="library-start-here">
                <div className="w-11 h-11 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: FFR.gold }}>
                  <ChevronRight size={22} style={{ color: FFR.forestDeep }} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-[10px] uppercase font-black tracking-[0.22em]" style={{ color: FFR.gold }}>Start here</div>
                  <div className="font-black uppercase text-white text-[15px] mt-0.5">Read this first — the START-HERE guide</div>
                </div>
                <button
                  onClick={downloadStartHere}
                  className="rounded-full px-5 py-2.5 font-black uppercase tracking-[0.14em] text-[12px] flex items-center gap-1.5"
                  style={{ background: FFR.gold, color: FFR.forestDeep }}
                  data-testid="library-start-here-download"
                >
                  <Download size={13} /> Download
                </button>
              </div>
            )}

            {choose3?.needs_picks && catalog && (
              <ChooseThreePicker
                picked={choose3.picked}
                picksRemaining={choose3.picks_remaining}
                ownedSkus={entitlements?.toolkit_skus || []}
                catalog={catalog}
                onSaved={load}
              />
            )}

            {replay?.owns_replay && (
              <div className="mb-6 rounded-3xl p-6 lg:p-7 relative overflow-hidden" style={{ background: `linear-gradient(135deg, ${FFR.forest} 0%, ${FFR.forestDeep} 100%)`, border: `2px solid ${FFR.gold}55` }} data-testid="library-replay-card">
                <div className="absolute -top-16 -right-16 w-40 h-40 rounded-full opacity-20 blur-3xl" style={{ background: FFR.gold }} />
                <div className="flex items-center gap-2 flex-wrap">
                  <div className="text-[10px] uppercase font-black tracking-[0.22em]" style={{ color: FFR.gold }}>Masterclass replay</div>
                  {replay.watched && (
                    <span
                      className="text-[10px] uppercase font-black tracking-[0.16em] px-2.5 py-1 rounded-full flex items-center gap-1"
                      style={{ background: FFR.gold, color: FFR.forestDeep }}
                      data-testid="library-replay-watched-pill"
                    >
                      <CheckCircle2 size={11} /> You watched the replay
                    </span>
                  )}
                </div>
                <h2 className="mt-2 font-black uppercase leading-tight text-white" style={{ fontSize: "clamp(22px, 2.6vw, 30px)" }}>
                  Beyond the Grant™ <span style={{ color: FFR.gold }}>Replay + Workbook</span>
                </h2>
                <p className="mt-2 text-[14px]" style={{ color: "rgba(255,255,255,0.8)" }}>
                  Watch the full masterclass on YouTube and download the companion workbook to work through it at your own pace.
                </p>
                <div className="mt-5 flex flex-wrap gap-3">
                  {replay?.youtube_url && (
                    <a
                      href={replay.youtube_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="rounded-full px-5 py-2.5 font-black uppercase tracking-[0.14em] text-[12px] flex items-center gap-1.5"
                      style={{ background: FFR.gold, color: FFR.forestDeep }}
                      data-testid="library-replay-watch"
                    >
                      <ExternalLink size={13} /> Watch on YouTube
                    </a>
                  )}
                  <button
                    onClick={downloadReplayWorkbook}
                    className="rounded-full px-5 py-2.5 font-black uppercase tracking-[0.14em] text-[12px] flex items-center gap-1.5 border-2"
                    style={{ borderColor: FFR.gold, color: FFR.gold }}
                    data-testid="library-replay-workbook-download"
                  >
                    <Download size={13} /> Download workbook
                  </button>
                  {!replay.watched && (
                    <button
                      onClick={markReplayWatched}
                      className="rounded-full px-5 py-2.5 font-bold uppercase tracking-[0.14em] text-[12px] flex items-center gap-1.5 hover:bg-white/5 transition-colors"
                      style={{ color: "rgba(255,255,255,0.75)", border: "2px solid rgba(255,255,255,0.2)" }}
                      data-testid="library-replay-mark-watched"
                    >
                      <CheckCircle2 size={13} /> I finished watching
                    </button>
                  )}
                </div>
                {replay.watched && (
                  <div className="mt-6 pt-5 border-t" style={{ borderColor: `${FFR.gold}33` }} data-testid="library-replay-upsell">
                    <div className="text-[10px] uppercase font-black tracking-[0.22em] mb-2" style={{ color: FFR.gold }}>Ready for the next step?</div>
                    <div className="flex flex-col sm:flex-row sm:items-end gap-4">
                      <div className="flex-1">
                        <div className="font-black text-white text-[17px] leading-tight uppercase">
                          Turn the replay into a funded plan — <span style={{ color: FFR.gold }}>with Donja</span>.
                        </div>
                        <p className="mt-1.5 text-[13.5px]" style={{ color: "rgba(255,255,255,0.72)" }}>
                          Bring your one biggest question to the monthly Group Session — first call is $99 and the roadmap is built <em>with</em> you, not for you.
                        </p>
                      </div>
                      <Link
                        to="/funding-readiness#work-with-me"
                        className="rounded-full px-5 py-2.5 font-black uppercase tracking-[0.14em] text-[12px] flex items-center gap-1.5 flex-shrink-0"
                        style={{ background: FFR.gold, color: FFR.forestDeep }}
                        data-testid="library-replay-upsell-cta"
                      >
                        See what&apos;s included <ArrowRight size={13} />
                      </Link>
                    </div>
                  </div>
                )}
              </div>
            )}

            {toolkits.length > 0 && (
              <div className="space-y-4 mb-8" data-testid="library-toolkits-list">
                {toolkits.map((tk) => (
                  <ToolkitCard key={tk.toolkit_sku} toolkit={tk} onReviewClick={setReviewFor} />
                ))}
              </div>
            )}

            {purchases && purchases.length > 0 && (
              <div className="mt-8 rounded-2xl p-5 border" style={{ background: "rgba(255,255,255,0.03)", borderColor: `${FFR.gold}33` }}>
                <div className="text-[10px] uppercase font-black tracking-[0.22em] mb-3" style={{ color: FFR.gold }}>Purchase history</div>
                <div className="space-y-2" data-testid="library-purchases-list">
                  {purchases.map((p) => <PurchaseRow key={p.event_id} purchase={p} />)}
                </div>
              </div>
            )}

            <p className="mt-6 text-[12.5px]" style={{ color: "rgba(255,255,255,0.55)" }}>
              Tip: click any toolkit to expand its file list. Preview button opens a PDF; Download saves the editable Word/Excel file.
            </p>
          </>
        )}

        <CatalogCard />
      </main>
      {reviewFor && (
        <ReviewModal
          toolkit={reviewFor}
          onClose={() => setReviewFor(null)}
          onSubmitted={() => setReviewFor(null)}
        />
      )}
    </BrandShell>
  );
}

/* --------------------------- Route shell --------------------------- */

export default function Library() {
  const navigate = useNavigate();
  const auth = useAuth(navigate);

  useEffect(() => {
    const prev = document.title;
    document.title = "My Library — Freedom Funding Readiness™";
    return () => { document.title = prev; };
  }, []);

  // Loading state (auth check in flight)
  if (auth.me === null) {
    return (
      <BrandShell>
        <div className="flex items-center justify-center py-24">
          <Loader2 className="animate-spin" size={22} style={{ color: FFR.gold }} />
        </div>
      </BrandShell>
    );
  }

  return (
    <Routes>
      <Route path="/" element={auth.me ? <LibraryHome auth={auth} /> : <Navigate to="/library/login" replace />} />
      <Route path="/login" element={auth.me ? <Navigate to="/library" replace /> : <Login auth={auth} />} />
      <Route path="/signup" element={auth.me ? <Navigate to="/library" replace /> : <Signup auth={auth} />} />
      <Route path="/forgot" element={<Forgot />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="*" element={<Navigate to="/library" replace />} />
    </Routes>
  );
}
